import axios from 'axios';
import { fixMojibakeIfNeeded } from '../utils/textFix';

// Get API base - throws if missing (no silent fallback)
const API_BASE = (() => {
  const base = import.meta.env.VITE_API_BASE;
  if (!base) {
    const err = '[CONFIG ERROR] Missing VITE_API_BASE. Check .env file.';
    console.error(err);
    throw new Error(err);
  }
  console.log(`[CRM] VITE_API_BASE=${base}`);
  return base.replace(/\/+$/, ''); // Remove trailing slash
})();

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const accessToken = localStorage.getItem('accessToken');
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// Handle responses - fix mojibake + handle 401
api.interceptors.response.use(
  (response) => {
    // Fix mojibake (UTF-8 misread as Latin-1) if detected
    if (response.data) {
      response.data = fixMojibakeIfNeeded(response.data);
    }
    // DEV: Log encoding info
    if (import.meta.env.DEV) {
      console.info('[ENC]', typeof response.data, response.headers?.['content-type']);
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (email: string, password: string) =>
  api.post('/auth/login', { email, password });

// Leads
export const getLeads = (params?: any) => api.get('/leads', { params });
export const getLead = (id: string) => api.get(`/leads/${id}`);
export const createLead = (data: any) => api.post('/leads', data);
export const updateLead = (id: string, data: any) => api.patch(`/leads/${id}`, data);
export const deleteLead = (id: string) => api.delete(`/leads/${id}`);

// Lead Notes
export const getLeadNotes = (leadId: string) => api.get(`/leads/${leadId}/notes`);
export const addLeadNote = (leadId: string, content: string) =>
  api.post(`/leads/${leadId}/notes`, { content });

// Lead Tasks (CRM)
export interface CreateTaskPayload {
  title: string;
  due_at: string; // YYYY-MM-DD HH:MM:SS
  channel: 'call' | 'whatsapp' | 'sms';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  notes?: string | null;
}

export interface UpdateTaskPayload {
  title?: string;
  due_at?: string;
  channel?: 'call' | 'whatsapp' | 'sms';
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  notes?: string;
  status?: 'open' | 'done' | 'cancelled';
  completed_at?: string;
}

export const getLeadTasks = (leadId: string, status?: string) =>
  api.get(`/crm/leads/${leadId}/tasks`, { params: status ? { status } : undefined });

export const addLeadTask = (leadId: string, data: CreateTaskPayload) =>
  api.post(`/crm/leads/${leadId}/tasks`, data);

export const updateLeadTask = (leadId: string, taskId: string, data: any) =>
  api.patch(`/leads/${leadId}/tasks/${taskId}`, data);

export const updateTask = (taskId: string | number, data: UpdateTaskPayload) =>
  api.patch(`/crm/tasks/${taskId}`, data);

// Messaging
export const sendWhatsApp = (leadId: string, message: string, templateId?: string) =>
  api.post('/messages/whatsapp/send', { lead_id: leadId, message, template_id: templateId });

export const sendSMS = (leadId: string, message: string, templateId?: string) =>
  api.post('/messages/sms/send', { lead_id: leadId, message, template_id: templateId });

export const getMessageTemplates = (channel?: string) =>
  api.get('/messages/templates', { params: { channel } });

// Call Logs
export const logCall = (leadId: string, data: any) =>
  api.post(`/leads/${leadId}/calls`, data);

export const getCallLogs = (leadId: string) => api.get(`/leads/${leadId}/calls`);

// Dashboard
export const getDashboardStats = () => api.get('/leads/dashboard/stats');


// CRM Messaging (via Templates + Outbox)
export interface SendMessagePayload {
  channel: 'whatsapp' | 'sms';
  lang?: 'ar' | 'he' | 'en';
  template_id?: number | null;
  free_text?: string | null;
  template_data?: Record<string, any>;
  dry_run?: boolean;
}

export const sendLeadMessage = (leadId: string, payload: SendMessagePayload) =>
  api.post(`/crm/leads/${leadId}/messages`, payload);

export const getLeadMessages = (leadId: string, limit?: number) =>
  api.get(`/crm/leads/${leadId}/messages`, { params: { limit } });

// Call Logging
export interface LogCallPayload {
  direction: 'outgoing' | 'incoming';
  outcome: 'answered' | 'no_answer' | 'busy' | 'voicemail' | 'wrong_number' | 'other';
  duration_seconds?: number | null;
  notes?: string | null;
  follow_up_at?: string | null;
  follow_up_channel?: 'call' | 'whatsapp' | 'sms' | null;
}

export const logLeadCall = (leadId: string, payload: LogCallPayload) =>
  api.post(`/crm/leads/${leadId}/calls`, payload);

// Timeline
export const getLeadTimeline = (leadId: string, limit?: number) =>
  api.get(`/crm/leads/${leadId}/timeline`, { params: { limit } });

// Templates (filtered)
export const getTemplates = (params?: { channel?: string; language?: string; is_active?: number }) =>
  api.get('/templates', { params });


// Dashboard Tasks
export interface DashboardTask {
  id: number;
  lead_id: number;
  lead_name: string | null;
  lead_phone: string | null;
  title: string;
  due_at: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  channel: 'call' | 'whatsapp' | 'sms';
}

export interface DashboardTasksResponse {
  success: boolean;
  data: {
    overdue_count: number;
    today_count: number;
    upcoming_count: number;
    overdue: DashboardTask[];
    today: DashboardTask[];
  };
}

export const getDashboardTasks = (params?: { tz?: string; limit?: number }) =>
  api.get<DashboardTasksResponse>('/crm/dashboard/tasks', { params });


// Pipeline / Stages
export interface Stage {
  id: number;
  stage_key: string;
  name_ar: string;
  name_he: string;
  color: string;
  sort_order: number;
  is_active: number;
}

export interface StagesResponse {
  success: boolean;
  data: Stage[];
}

export const getStages = () => api.get<StagesResponse>('/crm/stages');

export interface UpdateStagePayload {
  stage_id: number;
  notes?: string;
}

export interface UpdateStageResponse {
  success: boolean;
  data: {
    lead_id: number;
    from_stage: { id: number; key: string; name_ar: string } | null;
    to_stage: { id: number; key: string; name_ar: string };
    changed_by: number;
    notes?: string;
  };
  automation?: {
    triggered: boolean;
    noop?: boolean;
    reason?: string;
    outbox_id?: number;
    message_log_id?: number;
    template_language?: string;
  };
}

export const updateLeadStage = (leadId: number, payload: UpdateStagePayload) =>
  api.patch<UpdateStageResponse>(`/crm/leads/${leadId}/stage`, payload);

// Hot Leads / Lead Scoring
export interface ScoreBreakdown {
  factor: string;
  points: number;
  reason: string;
}

export interface HotLead {
  id: string;
  first_name: string | null;
  last_name: string | null;
  phone: string;
  email: string | null;
  status: string;
  source: string;
  utm_source: string | null;
  created_at: string;
  last_contact_at: string | null;
  last_response_at: string | null;
  estimated_value: number | null;
  notes_count: number;
  calls_count: number;
  messages_count: number;
  score: number;
  score_label: 'hot' | 'warm' | 'cold';
  breakdown: ScoreBreakdown[];
}

export interface HotLeadsResponse {
  leads: HotLead[];
  total: number;
  limit: number;
  min_score: number;
  thresholds: { hot: number; warm: number; cold: number };
  scoring_config: { max_possible: number; factors: string[] };
}

export const getHotLeads = (params?: { limit?: number; min_score?: number }) =>
  api.get<HotLeadsResponse>('/crm/leads/hot', { params });

export const getLeadScore = (leadId: string) =>
  api.get(`/crm/leads/${leadId}/score`);

// Upsell/Cross-sell suggestions
export interface UpsellSuggestRequest {
  placement: 'pdp' | 'cart' | 'crm';
  lead_id?: number;
  stage_id?: number;
}

export interface ProductSuggestion {
  product_id: number;
  reason: string;
  rule_id: number;
  score: number;
  product?: {
    id: number;
    name_ar: string;
    name_he: string;
    price: number;
    image_url?: string;
  };
}

export interface UpsellSuggestResponse {
  suggestions: ProductSuggestion[];
  message: { ar: string; he: string };
  meta: {
    applied_rules: number[];
    deterministic: boolean;
  };
}

export const getUpsellSuggestions = (data: UpsellSuggestRequest) =>
  api.post<UpsellSuggestResponse>('/marketing/upsell/suggest', data);

export const trackUpsellEvent = (data: {
  rule_id: number;
  placement: string;
  event: 'impression' | 'click' | 'add_to_cart';
}) => api.post('/marketing/upsell/track', data);

// Orders (CRM view)
export const getOrders = (params?: { search?: string; status?: string; page?: number; limit?: number }) =>
  api.get('/orders/admin/list', { params });

export const getOrder = (id: string) => api.get(`/orders/admin/${id}`);

// Campaigns (CRM view)
export const getCampaigns = (params?: { search?: string; status?: string }) =>
  api.get('/crm/campaigns', { params });

// Messages log
export const getMessages = (params?: { search?: string; channel?: string; limit?: number }) =>
  api.get('/crm/messages', { params });

// Calls log
export const getCalls = (params?: { search?: string; outcome?: string; limit?: number }) =>
  api.get('/crm/calls', { params });

// Reports
export const getReportsStats = (params?: { range?: string }) =>
  api.get('/crm/reports/stats', { params });

// Settings
export const getSettings = () => api.get('/settings');
export const updateSettings = (data: any) => api.patch('/settings', data);

export default api;


